// PRD Part B — license tiers, cumulative: T0 ⊃ T1 ⊃ T2 ⊃ T3.
// Lower numeric rank = more capability (T0 = 0 = most).
export const TIER = {
  T0_ADMIN: 'T0',
  T1_FULL: 'T1',
  T2_PAYG: 'T2',
  T3_LITE: 'T3'
}

const RANK = { [TIER.T0_ADMIN]: 0, [TIER.T1_FULL]: 1, [TIER.T2_PAYG]: 2, [TIER.T3_LITE]: 3 }

export const CAPABILITY = {
  SHOW_PRIMARY_VECTORS: 'show_primary_vectors',
  SHOW_SUBVECTORS: 'show_subvectors',
  EXPORT_QUARANTINED: 'export_quarantined',
  // Toggling vectors recomputes the quarantine set from raw per-vector scores. Below T0 the
  // Data Centrifuge is a read-only account of what the automated run decided, not a live
  // recalculation surface — so selection and re-run are admin-only.
  SELECT_VECTORS: 'select_vectors',
  REVIEW_EVIDENCE: 'review_evidence',
  RELEASE_QUARANTINE: 'release_quarantine',
  // Advisory reinstates responses the run declined to score. Reading it means holding the vector
  // selection that reveals it, so it unlocks with the administrative surfaces, not below them.
  SHOW_ADVISORY: 'show_advisory',
  // Filtering the stats page to the responses the report names. The Data Centrifuge section covers
  // only the respondents that filter selects, so the Filters panel opens beside the report instead
  // of closing it.
  FILTER_RESPONSES: 'filter_responses',
  // An admin comes to the report to decide on flagged responses, so it opens on the Data Centrifuge
  // with the ones still awaiting a decision. Below T0 the reader comes to read the report, which
  // opens on the panel it is built on.
  OPEN_ON_CENTRIFUGE: 'open_on_centrifuge'
}

// Minimum tier (max rank) that unlocks each capability.
const UNLOCKED_AT = {
  [CAPABILITY.SHOW_PRIMARY_VECTORS]: TIER.T3_LITE,
  [CAPABILITY.SHOW_SUBVECTORS]: TIER.T2_PAYG,
  [CAPABILITY.EXPORT_QUARANTINED]: TIER.T2_PAYG,
  [CAPABILITY.REVIEW_EVIDENCE]: TIER.T1_FULL,
  [CAPABILITY.SELECT_VECTORS]: TIER.T0_ADMIN,
  [CAPABILITY.RELEASE_QUARANTINE]: TIER.T0_ADMIN,
  [CAPABILITY.SHOW_ADVISORY]: TIER.T0_ADMIN,
  [CAPABILITY.FILTER_RESPONSES]: TIER.T0_ADMIN,
  [CAPABILITY.OPEN_ON_CENTRIFUGE]: TIER.T0_ADMIN
}

export const can = (tier, capability) => RANK[tier] <= RANK[UNLOCKED_AT[capability]]

// Evidence review is the one capability whose data the producer decides separately: it scrubs the
// per-respondent detail out of the payload before sending it, and states on the report whether it
// granted the capability. Gating on the tier alone lets the affordance outrun the data — a Review
// Evidence link over rows that were never sent. So both must agree, exactly as the export
// affordance already tracks the server's own export flag. An absent grant reads as granted, so a
// payload predating the flag is never wrongly denied; only an explicit denial withholds.
export const canReviewEvidence = (tier, reviewEvidence) =>
  can(tier, CAPABILITY.REVIEW_EVIDENCE) && false !== reviewEvidence

// Both report controls that filter the stats page to its responses (the Data Centrifuge toggle and the
// sidebar Filter action) need the tier and the stats-page filters themselves. A feature map that does
// not name the filters offers none, so an absent entry withholds the control rather than granting it.
export const canFilterResponses = (tier, features) =>
  can(tier, CAPABILITY.FILTER_RESPONSES) && Boolean(features?.advanced_filters) && !features.advanced_filters.disabled

// Wire integer emitted by the data_quality_report feature (Subscription::Feature::DataQualityReport:
// admin 0, full 1, payg 2, lite 3). The feature is viewer-aware — admin/consultant are already
// collapsed to 0 on the backend — so the wire value is the effective tier. Unknown/absent -> Lite.
const WIRE_TO_TIER = { 0: TIER.T0_ADMIN, 1: TIER.T1_FULL, 2: TIER.T2_PAYG, 3: TIER.T3_LITE }
export const tierFromWire = wire => WIRE_TO_TIER[wire]

// The feature already encodes the effective tier; just default the most restrictive (Lite) when absent.
export const resolveTier = ({ packageTier } = {}) => packageTier || TIER.T3_LITE

// Single source for both entry points (page shim + sidebar panel): resolve the effective tier
// straight from the stats feature map.
export const tierFromFeatures = features =>
  resolveTier({ packageTier: tierFromWire(features?.data_quality_report?.value?.tier) })

// A guest's report is view-only: the backend clears this flag (false) so the panel hides Export while
// still granting the tier's view capabilities. Absent -> allowed (an authorized viewer).
export const exportAllowedFromFeatures = features => features?.data_quality_report?.value?.export !== false
